import { postAddToCart } from '~js/utils/variant';
import { deliveryDrawer } from './delivery-drawer';
import { storeLocater } from './store-locater';
import { delivery } from '~js/utils/delivery';

const productInfo = {
  selectors: {
    buybox: '.js-buybox',
    product: '.js-buybox__product',
    title: '.js-buybox__product-title',
    price: '.js-buybox__product_price',
    editDelivery: '.js-edit-delivery',
    variantCheckbox: '.js__buybox-variant-checkbox',
    variantOption: '.variant-option',
    qtyMinus: '.js-qty-minus',
    qtyPlus: '.js-qty-plus',
    qtyInput: '.js-qty-input',
    addToCart: '.js-buybox__product-addToCartBtn',
    overlay: '.js-delivery-overlay',
    deliveryDrawer: '.js-delivery-drawer',
    closeDrawer: '.js-close-delivery-drawer',
    variantWeight: '.js-variant-weight',
    currentVariantId: null,
  },

  el: {},
  quantityMap: new Map(),

  qs(selector, parent = document) {
    return parent.querySelector(selector);
  },

  qsAll(selector, parent = document) {
    return Array.from(parent.querySelectorAll(selector));
  },

  initializeElements() {
    const { selectors: s, qs } = this;
    this.quantityMap.clear();
    this.qsAll(s.variantCheckbox).forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.initializeQuantityMap();
    this.el.buybox = qs(s.buybox);
    if (!this.el.buybox) return;

    const product = qs(s.product, this.el.buybox);
    if (!product) return;

    this.el.product = product;
    this.el.title = qs(s.title, product);
    this.el.price = qs(s.price, product);
    this.el.overlay = qs(s.overlay);
    this.el.drawer = qs(s.deliveryDrawer);
    this.el.closeDrawer = qs(s.deliveryDrawer);
    this.el.addToCart = qs(s.addToCart, this.deliverableContainer);
    this.el.editDelivery = qs(s.editDelivery, this.el.buybox);
    this.el.variantWeight = qs(s.variantWeight, this.el.buybox);

    storeLocater.init(this.el.buybox);

    this.initVariantCheckboxBorders();
    this.initQuantityControls();
    this.currentVariantId = this.getCurrentVariant()?.id || null;
  },

  async initializeEvents() {
    this.initializeElements();
    const params = new URLSearchParams(window.location.search);
    const zipCode = params.get('z');
    if (zipCode) {
      this.checkZipAndInitialize(zipCode);
    } else if (window.customerData.isLoggedIn) {
      await this.checkLoggedInUser();
    } else {
      const container = document.querySelector(
        '.js-product__locateMeContainer',
      );
      const loader = document.querySelector('.js-locateme_loader');
      delivery.hide(loader);
      delivery.show(container);
    }
    const { el } = this;

    el.addToCart?.addEventListener('click', () => this.handleAddToCart());
    el.editDelivery?.addEventListener('click', () =>
      this.openDeliveryOverlay(),
    );
    el.closeDrawer?.addEventListener('click', (e) => {
      // Only close if clicked directly on the overlay (not the inner drawer)
      if (e.target === el.closeDrawer) {
        e.preventDefault();
        this.closeDeliveryOverlay();
      }
    });
  },

  async checkLoggedInUser() {
    const { isLoggedIn, defaultAddress } = window.customerData || {};
    if (!isLoggedIn || !defaultAddress?.zipCode) return false;
    const container = document.querySelector('.js-product__locateMeContainer');
    const loader = document.querySelector('.js-locateme_loader');
    delivery.show(loader);
    delivery.hide(container);
    const { zipCode } = defaultAddress;
    const { zip, deliverable } =
      await delivery.handleLocateManualGeneric(zipCode);
    if (deliverable === true) {
      delivery.hide(this.el.locateMeContainer);
      delivery.hide(this.el.ctas);
      storeLocater.toggleDeliverability({ zip, deliverable });
      delivery.hide(this.el.availabilityText);
    }
    delivery.hide(loader);
    delivery.show(container);
  },

  openDeliveryOverlay() {
    deliveryDrawer.initializeEvents();
    deliveryDrawer.openDeliveryOverlay();
  },

  closeDeliveryOverlay() {
    deliveryDrawer.closeDeliveryOverlay();
  },

  initVariantCheckboxBorders() {
    const { selectors: s, qsAll } = this;
    const urlParams = this.parseUrlParameters();

    // Initialize from URL parameters
    urlParams.selectedItems.forEach((item) => {
      const checkbox = this.qs(`[value="${item.variantId}"]`);
      if (checkbox) {
        checkbox.checked = true;
        this.quantityMap.set(item.variantId, item.quantity);
      }
    });

    // Setup event listeners
    qsAll(s.variantCheckbox).forEach((checkbox) => {
      const variantId = checkbox.value;

      checkbox.addEventListener('change', () => this.updateCheckboxBorders());

      checkbox.addEventListener('dblclick', (e) => {
        e.preventDefault();
        this.quantityMap.set(variantId, 0);
        checkbox.checked = false;
        this.updateCheckboxBorders();

        // Update UI immediately
        const parent = checkbox.closest(s.variantOption);
        if (parent) {
          const display = parent.querySelector(s.qtyInput);
          const plusBtn = parent.querySelector(s.qtyPlus);
          const minusBtn = parent.querySelector(s.qtyMinus);
          this.updateDisplay(0, plusBtn, minusBtn, display);
        }
      });
    });

    this.updateCheckboxBorders();
  },

  // Update variant selections and UI
  updateCheckboxBorders() {
    const { selectors: s, qsAll } = this;
    const selectedItems = [];

    // Reset all UI states
    qsAll(s.variantOption).forEach((el) => {
      el.classList.remove('is-checked', 'border-blue-400');
      el.classList.add('border-gray-200');
      el.querySelectorAll('.js-product-radio').forEach((icon) =>
        icon.classList.remove('is-checked'),
      );
    });

    // Process selected variants
    qsAll(s.variantCheckbox).forEach((checkbox) => {
      const variantId = checkbox.value;
      const parent = checkbox.closest(s.variantOption);

      if (checkbox.checked && parent) {
        // Update UI state
        parent.classList.add('is-checked', 'border-blue-400');
        parent.classList.remove('border-gray-200');
        parent
          .querySelectorAll('.js-product-radio')
          .forEach((icon) => icon.classList.add('is-checked'));

        // Ensure minimum quantity
        let quantity = this.quantityMap.get(variantId) || 0;
        if (quantity < 1) {
          quantity = 1;
          this.quantityMap.set(variantId, quantity);
        }

        // Store selection
        selectedItems.push({ variantId, quantity });

        // Update display - FIXED: Added maxLimit and limitMessage parameters
        const display = parent.querySelector(s.qtyInput);
        const plusBtn = parent.querySelector(s.qtyPlus);
        const minusBtn = parent.querySelector(s.qtyMinus);

        // Get maxLimit and limitMessage from DOM
        const maxLimit = parseInt(checkbox.dataset.maxLimit, 10) || null;
        const limitMessage = parent.querySelector('.variant-limit-message');

        const maxQty = parseInt(checkbox?.dataset.inventory || '9999', 10);
        this.updateDisplay(
          quantity,
          plusBtn,
          minusBtn,
          display,
          maxLimit,
          limitMessage,
          maxQty,
        );
      }
    });

    // Update URL and prices
    this.updateUrlWithSelectedItems(selectedItems);
    this.updateVariantPrice();
    this.updateAddToCartState();
  },

  // Update URL with selected items while preserving existing parameters
  updateUrlWithSelectedItems(selectedItems) {
    const url = new URL(window.location);

    // Create a new URLSearchParams to preserve existing parameters
    const params = new URLSearchParams(url.search);

    // Remove only variant-related parameters
    params.delete('variants');
    params.delete('quantities');

    // Add new variant parameters
    selectedItems.forEach((item) => {
      params.append('variants', item.variantId);
      params.append('quantities', item.quantity);
    });

    // Update URL without reloading
    url.search = params.toString();
    window.history.replaceState({}, '', url.toString());
  },

  // Parse URL parameters into selected items
  parseUrlParameters() {
    const params = new URLSearchParams(window.location.search);
    const variants = params.getAll('variants');
    const quantities = params.getAll('quantities').map(Number);

    const selectedItems = [];
    for (let i = 0; i < variants.length; i++) {
      selectedItems.push({
        variantId: variants[i],
        quantity: quantities[i] || 1,
      });
    }

    return { selectedItems };
  },

  initQuantityControls() {
    const containers = document.querySelectorAll('.js-product-quantity');

    containers.forEach((container) => {
      const minusBtn = container.querySelector('.js-qty-minus');
      const plusBtn = container.querySelector('.js-qty-plus');
      const display = container.querySelector('.js-qty-input');
      const checkbox = container
        .closest('.variant-option')
        ?.querySelector('.js__buybox-variant-checkbox');
      const variantId = checkbox?.value;

      if (!variantId) return;

      const maxLimit = parseInt(checkbox.dataset.maxLimit, 10) || null;
      const limitMessage = container
        .closest('.variant-option')
        .querySelector('.variant-limit-message');

      if (limitMessage) limitMessage.style.display = 'none';

      const maxQty = parseInt(checkbox?.dataset.inventory || '9999', 10);

      let quantity = this.quantityMap.get(variantId) || 0;

      if (maxQty === 0) {
        this.quantityMap.set(variantId, 0);
        this.updateDisplay(
          0,
          plusBtn,
          minusBtn,
          display,
          maxLimit,
          limitMessage,
          maxQty,
        );
        return;
      }

      if (maxLimit && quantity > maxLimit) {
        quantity = maxLimit;
        this.quantityMap.set(variantId, quantity);
        this.updateUrlWithSelectedItems(
          Array.from(this.quantityMap, ([id, qty]) => ({
            variantId: id,
            quantity: qty,
          })),
        );
      }

      this.updateDisplay(
        quantity,
        plusBtn,
        minusBtn,
        display,
        maxLimit,
        limitMessage,
        maxQty,
      );

      plusBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const currentQty = this.quantityMap.get(variantId) || 0;

        if (quantity > maxQty) {
          quantity = maxQty;
          return;
        }

        // If already at max, just stop and maybe shake the button for feedback
        if (maxLimit && currentQty >= maxLimit) {
          return;
        }

        const newQty = currentQty + 1;
        this.quantityMap.set(variantId, newQty);
        this.updateDisplay(
          newQty,
          plusBtn,
          minusBtn,
          display,
          maxLimit,
          limitMessage,
          maxQty,
        );

        if (!checkbox.checked) {
          checkbox.checked = true;
          this.updateCheckboxBorders();
        } else {
          this.updateVariantPrice();
          this.updateAddToCartState();
          this.updateCheckboxBorders();
        }
      });

      minusBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        quantity = Math.max(0, (this.quantityMap.get(variantId) || 0) - 1);
        this.quantityMap.set(variantId, quantity);
        this.updateDisplay(
          quantity,
          plusBtn,
          minusBtn,
          display,
          maxLimit,
          limitMessage,
          maxQty,
        );

        if (quantity === 0 && checkbox.checked) {
          checkbox.checked = false;
          this.updateCheckboxBorders();
        } else {
          this.updateVariantPrice();
          this.updateAddToCartState();
          this.updateCheckboxBorders();
        }
      });

      checkbox.addEventListener('change', (e) => {
        if (e.target.disabled) return;
        if (!checkbox.checked) {
          this.quantityMap.set(variantId, 0);
          this.updateDisplay(
            0,
            plusBtn,
            minusBtn,
            display,
            maxLimit,
            limitMessage,
            maxQty,
          );
        } else if (this.quantityMap.get(variantId) === 0) {
          this.quantityMap.set(variantId, 1);
          this.updateDisplay(
            1,
            plusBtn,
            minusBtn,
            display,
            maxLimit,
            limitMessage,
            maxQty,
          );
        }
        this.updateCheckboxBorders();
      });
    });
  },

  initializeQuantityMap() {
    const { qsAll } = this;
    const radios = qsAll(this.selectors.variantRadio);
    const urlParams = this.parseUrlParameters();

    radios.forEach((radio) => {
      let initialQty =
        radio.value === urlParams.variant ? urlParams.quantity : 0;

      if (radio.value === urlParams.variant && initialQty === 0) {
        initialQty = 1;
      }

      if (!this.quantityMap.has(radio.value)) {
        this.quantityMap.set(radio.value, initialQty);
      }
    });
  },

  updateVariantPrice() {
    const { selectors: s } = this;
    let totalPrice = 0;

    // Calculate total price for all selected variants
    this.qsAll(s.variantCheckbox).forEach((checkbox) => {
      if (checkbox.checked) {
        const price = checkbox.dataset.price_formatted;
        if (price) {
          const rawPrice = price.replace(/[^0-9.]/g, '');
          const numericPrice = parseFloat(rawPrice);
          const variantId = checkbox.value;
          const quantity = this.quantityMap.get(variantId) || 0;

          if (!isNaN(numericPrice)) {
            totalPrice += numericPrice * quantity;
          }
        }
      }
    });

    // Update price displays
    const formattedTotal = totalPrice.toFixed(2);

    // Main price display
    if (this.el.price) {
      const priceElement = this.el.price.querySelector(
        '.js-buybox__product-currentPrice',
      );
      if (priceElement) {
        priceElement.textContent = `: $${formattedTotal}`;
      }
    }

    // Add to cart button price display
    if (this.el.addToCart) {
      const secondaryPriceElement = this.el.addToCart.querySelector(
        '.js-add-to-cart-variant-price',
      );
      const dash = this.el.addToCart.querySelector(
        '.js-variant-add-to-cart-dash',
      );

      if (secondaryPriceElement) {
        dash.classList.toggle('hidden', totalPrice === 0);
        secondaryPriceElement.textContent =
          totalPrice > 0 ? `$${formattedTotal}` : '';
      }
    }
  },

  updateDisplay(
    quantity,
    plusBtn,
    minusBtn,
    display,
    maxLimit = null,
    limitMessage = null,
    maxQty = null,
  ) {
    if (!display) return;
    if (quantity === 0) {
      display.classList.add('hidden');
      minusBtn?.classList.add('hidden');
      plusBtn?.classList.remove('hidden');
      minusBtn?.classList.remove('quantity-zero');
    } else {
      display.classList.remove('hidden');
      minusBtn?.classList.remove('hidden');
      if (quantity === 1) {
        minusBtn?.classList.add('quantity-zero');
      } else {
        minusBtn?.classList.remove('quantity-zero');
      }
    }

    const reachedMaxLimit = maxLimit && quantity >= maxLimit;
    const reachedMaxQty = maxQty && quantity >= maxQty;

    if (reachedMaxLimit || reachedMaxQty) {
      plusBtn.disabled = true;
      plusBtn.classList.add('opacity-20', 'cursor-not-allowed');
      plusBtn.classList.remove('cursor-pointer');
    } else {
      plusBtn.disabled = false;
      plusBtn.classList.remove('opacity-20', 'cursor-not-allowed');
      plusBtn.classList.add('cursor-pointer');
    }
    if (limitMessage && limitMessage.style) {
      if (reachedMaxLimit) {
        limitMessage.style.display = 'flex';
      } else {
        limitMessage.style.display = 'none';
      }
    }

    if (maxQty === 0) {
      display.classList.add('hidden');
      plusBtn.disabled = true;
      minusBtn.disabled = true;
      plusBtn.classList.add('opacity-20', 'cursor-not-allowed');
      minusBtn.classList.add('opacity-20', 'cursor-not-allowed');
    }
    display.textContent = quantity;
    this.updateAddToCartState?.();
    this.updateFlex?.();
  },

  incrementQuantity(quantity) {
    this.updateDisplay();
    return (quantity += 1);
  },

  decrementQuantity(quantity) {
    this.updateDisplay();
    return (quantity -= 1);
  },

  getCurrentVariant() {
    const radio = document.querySelector(
      `${this.selectors.variantRadio}:checked`,
    );
    return radio
      ? {
          id: radio.value,
          parent: radio.closest(this.selectors.variantOption),
        }
      : null;
  },

  // Add this method to update add to cart state
  updateAddToCartState() {
    let totalQuantity = 0;

    this.quantityMap.forEach((quantity) => {
      if (quantity > 0) {
        totalQuantity += quantity;
      }
    });

    if (this.el.addToCart) {
      const shouldDisable = totalQuantity === 0;
      this.el.addToCart.disabled = shouldDisable;

      const secondaryPriceElement = this.el.addToCart.querySelector(
        '.js-add-to-cart-variant-price',
      );
      if (secondaryPriceElement) {
        secondaryPriceElement.classList.toggle('hidden', shouldDisable);
      }
    }
  },

  handleAddToCart() {
    const items = [];

    this.quantityMap.forEach((quantity, variantId) => {
      if (quantity > 0) {
        items.push({ id: variantId, quantity });
      }
    });

    if (items.length === 0) {
      return alert('Please select at least one variant and quantity.');
    }

    // Post all items to cart
    postAddToCart(items, null);
  },

  async checkZipAndInitialize(zipCode) {
    if (zipCode) {
      const container = document.querySelector(
        '.js-product__locateMeContainer',
      );
      const loader = document.querySelector('.js-locateme_loader');
      delivery.show(loader);
      delivery.hide(container);
      try {
        const { zip, deliverable } =
          await delivery.handleLocateManualGeneric(zipCode);
        if (deliverable === 'not-at-all') {
          delivery.redirectToNearestStoreFinder(zip);
        } else if (deliverable === true) {
          delivery.hide(this.el.locateMeContainer);
          delivery.hide(this.el.ctas);
          storeLocater.toggleDeliverability({ zip, deliverable });
          delivery.hide(this.el.availabilityText);
          return true;
        } else if (deliverable === false) {
          delivery.redirectToLegacyBuyBox(zip);
        }
      } catch (err) {
        console.error(err);
      } finally {
        const container = document.querySelector(
          '.js-product__locateMeContainer',
        );
        const loader = document.querySelector('.js-locateme_loader');
        delivery.hide(loader);
        delivery.show(container);
      }
    }
  },

  async fetchVariantData(variantId) {
    try {
      const response = await fetch(`/variants/${variantId}.json`);
      if (!response.ok) throw new Error('Variant not found');
      return await response.json();
    } catch (error) {
      console.error('Error fetching variant:', error);
      return null;
    }
  },

  async updateVariantWeight() {
    const { el } = this;
    if (!el.variantWeight || !this.currentVariantId) return;
    try {
      const variantData = await this.fetchVariantData(this.currentVariantId);
      if (variantData) {
        const weightGrams = variantData.product_variant.grams;
        const weightLbs = variantData.product_variant.weight;
        const weightUnit = variantData.product_variant.weight_unit;
        const weightKg = (weightGrams * 0.001).toFixed(1);

        el.variantWeight.textContent = `Net Wt. ${weightLbs} ${weightUnit} (${weightKg} kg)`;
      }
    } catch (error) {
      console.error('Error updating weight:', error);
    }
  },

  updateFlex() {
    const containers = document.querySelectorAll('.js-maxlimit-flex-grow');
    if (!containers.length) return;
    let anyReachedMax = false;
    containers.forEach((container) => {
      const checkbox = container.querySelector('.js__buybox-variant-checkbox');
      const maxLimit = parseInt(checkbox?.dataset.maxLimit || '0', 10);
      const qtyEl = container.querySelector('.js-qty-input');

      if (!qtyEl || !maxLimit) return;

      const quantity = parseInt(qtyEl.textContent.trim(), 10) || 0;
      if (quantity >= maxLimit) {
        anyReachedMax = true;
        container.classList.add('flex-1');
        container.classList.remove('h-full');
      } else {
        container.classList.remove('flex-1');
      }
    });
    if (!anyReachedMax) {
      containers.forEach((container) => {
        container.classList.add('h-full');
        container.classList.remove('flex-1');
      });
    } else {
      containers.forEach((container) => {
        container.classList.remove('h-full');
      });
    }
  },
};

export { productInfo };
